import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import SkeletonComponent from '@mui/material/Skeleton';
import React from 'react';
import styled from 'styled-components';
import { createBox } from '@famly/mf_layout_box';
import { useAriaAndDataProps } from '@famly/mf_ts-utils_data-aria-props';
const StyledBox = styled(createBox({})) `
    visibility: ${props => (props.$isVisible ? undefined : 'hidden')};
    box-sizing: border-box;
`;
const SimpleSkeleton = SkeletonComponent;
const DynamicSkeleton = (props) => {
    const { width, height, loading, disableDynamicSize, children, ...rest } = props;
    const [initialized, setInitialized] = React.useState(false);
    const [internalWidth, setInternalWidth] = React.useState(width);
    const [internalHeight, setInternalHeight] = React.useState(height);
    const childRef = React.useRef(null);
    const hiddenChildRef = React.useRef(null);
    const ariaDataProps = useAriaAndDataProps(rest);
    // Update dimensions based on actual content size
    const updateDimensions = React.useCallback(() => {
        const ref = loading ? hiddenChildRef : childRef;
        if (ref.current && !disableDynamicSize) {
            const dimensions = ref.current.getBoundingClientRect();
            if (dimensions.width > 0 && dimensions.height > 0) {
                setInternalWidth(dimensions.width);
                // Ensure we're using the full height of the element by rounding up to nearest integer
                setInternalHeight(Math.ceil(dimensions.height));
                setInitialized(true);
            }
        }
    }, [loading, disableDynamicSize]);
    // Initial measurement after DOM is ready
    React.useLayoutEffect(() => {
        if (!initialized) {
            updateDimensions();
        }
    }, [initialized, updateDimensions]);
    // Re-measure when loading state changes
    React.useEffect(() => {
        updateDimensions();
    }, [loading, updateDimensions]);
    // Handle resize events
    React.useEffect(() => {
        window.addEventListener('resize', updateDimensions);
        return () => {
            window.removeEventListener('resize', updateDimensions);
        };
    }, [updateDimensions]);
    return (_jsxs(_Fragment, { children: [loading ? (_jsx(SkeletonComponent, { ...ariaDataProps, width: internalWidth, height: internalHeight, ...rest })) : null, _jsx(StyledBox, { ref: childRef, display: "inline-block", position: loading ? 'absolute' : undefined, zIndex: loading ? -1 : undefined, "$isVisible": !loading, children: children })] }));
};
/**
 * A versatile Skeleton component that can be used in two modes:
 * 1. Simple mode - Works like MUI Skeleton when no children are provided
 * 2. Dynamic mode - Auto-sizes to match the dimensions of its children
 *
 * The component provides a loading state placeholder that can automatically
 * adapt to the size of the content it will eventually contain.
 *
 * @example Simple usage
 * ```tsx
 * <Skeleton width={200} height={40} />
 * ```
 *
 * @example Dynamic usage with content
 * ```tsx
 * <Skeleton loading={isLoading}>
 *   <Text>This content will be measured and the skeleton will match its size</Text>
 * </Skeleton>
 * ```
 */
export const Skeleton = (props) => props.children ? _jsx(DynamicSkeleton, { ...props }) : _jsx(SimpleSkeleton, { ...props });
